import React from 'react';

// ─── Design tokens ───────────────────────────────────────────────────────────
const C = {
  bg: '#08090f', card: '#0d1117', inp: '#0f1420',
  border: '#1f2937', borderS: '#1a2035',
  primary: '#e5e7eb', secondary: '#6b7280', muted: '#374151',
  profit: '#16C784', loss: '#ef4444', accent: '#6366f1',
};

// ─── Domain helpers ───────────────────────────────────────────────────────────
function scoreColor(s) {
  if (s >= 81) return '#16C784';
  if (s >= 61) return '#6366F1';
  if (s >= 41) return '#F59E0B';
  if (s >= 21) return '#f97316';
  return '#ef4444';
}
function scoreLabel(s) {
  if (s >= 81) return 'Elite Discipline';
  if (s >= 61) return 'Disciplined';
  if (s >= 41) return 'Developing';
  if (s >= 21) return 'Needs Work';
  return 'High Risk';
}
function tierBadge(s) {
  if (s >= 90) return { text: 'Top 5%',    color: '#16C784' };
  if (s >= 80) return { text: 'Top 15%',   color: '#06b6d4' };
  if (s >= 70) return { text: 'Top 30%',   color: '#6366f1' };
  if (s >= 60) return { text: 'Top 50%',   color: '#f59e0b' };
  return              { text: 'Bottom 50%', color: '#ef4444' };
}

const COMPONENT_LABELS = {
  stop_loss: 'Stop Loss', risk_reward: 'Risk-Reward', strategy: 'Strategy',
  emotion: 'Emotion', frequency: 'Frequency', confidence: 'Confidence',
  journal: 'Journaling', sl_violation: 'SL Violations', risk_profile: 'Risk Profile',
};
const MILESTONES = [
  { threshold: 25,  label: 'Basic' },
  { threshold: 50,  label: 'Developing' },
  { threshold: 65,  label: 'Consistent' },
  { threshold: 80,  label: 'Disciplined' },
  { threshold: 90,  label: 'Elite' },
  { threshold: 100, label: 'Perfect' },
];

// ─── Micro-components ─────────────────────────────────────────────────────────
function SDiv({ label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '6px 0' }}>
      <div style={{ flex: 1, height: 1, background: C.borderS }} />
      <span style={{ color: C.muted, fontSize: 9, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase' }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: C.borderS }} />
    </div>
  );
}

function SCard({ children, accent, style = {} }) {
  return (
    <div style={{
      borderRadius: 12, padding: '10px 12px', boxSizing: 'border-box',
      background: accent ? `linear-gradient(135deg,${accent}12 0%,${C.card} 100%)` : C.card,
      border: `1px solid ${accent ? `${accent}28` : C.border}`, ...style,
    }}>
      {children}
    </div>
  );
}

function SHead({ emoji, title, color }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
      <div style={{
        width: 20, height: 20, borderRadius: 6, background: `${color || C.accent}22`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 11,
      }}>
        {emoji}
      </div>
      <span style={{ color: C.primary, fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1 }}>{title}</span>
    </div>
  );
}

function MC({ label, value, color }) {
  return (
    <div style={{
      padding: '5px 8px', borderRadius: 8,
      background: color ? `${color}10` : C.inp,
      border: `1px solid ${color ? `${color}22` : C.border}`,
    }}>
      <div style={{ color: C.secondary, fontSize: 8, fontWeight: 600, marginBottom: 1 }}>{label}</div>
      <div style={{ color: color || C.primary, fontSize: 11, fontWeight: 800, lineHeight: 1.2 }}>{value ?? '—'}</div>
    </div>
  );
}

function Pill({ label, color }) {
  return (
    <span style={{
      display: 'inline-block', background: `${color}18`, color,
      border: `1px solid ${color}28`, borderRadius: 20, padding: '2px 8px',
      fontSize: 9, fontWeight: 700, marginRight: 3, marginBottom: 3,
    }}>
      {label}
    </span>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
function DisciplineShareCard({ discipline_score, component_scores, profile_type, cardRef }) {
  const overall     = discipline_score ?? 0;
  const color       = scoreColor(overall);
  const tier        = tierBadge(overall);
  const today       = new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

  const components  = Object.entries(component_scores || {})
    .map(([key, val]) => ({ key, label: COMPONENT_LABELS[key] || key, score: val ?? 0 }))
    .sort((a, b) => b.score - a.score);

  const strong = components.filter(c => c.score >= 70);
  const weak   = components.filter(c => c.score < 70).slice(0, 4);

  const next = MILESTONES.find(m => m.threshold > overall);
  const prev = [...MILESTONES].reverse().find(m => m.threshold <= overall);

  const circumference = 2 * Math.PI * 21;
  const dash          = (overall / 100) * circumference;

  // Milestone progress width (guard divide-by-zero)
  const milestoneWidth = (next && prev && next.threshold !== prev.threshold)
    ? ((overall - prev.threshold) / (next.threshold - prev.threshold)) * 100
    : 0;

  return (
    <div
      ref={cardRef}
      style={{
        width: 1080, height: 1350, background: C.bg, overflow: 'hidden', position: 'relative',
        fontFamily: "'Segoe UI',system-ui,-apple-system,sans-serif",
        display: 'flex', flexDirection: 'column', padding: 28, gap: 8, boxSizing: 'border-box',
      }}
    >
      {/* Ambient glow */}
      <div style={{
        position: 'absolute', top: -80, right: -80, width: 220, height: 220,
        borderRadius: '50%', background: `radial-gradient(circle,${color}0e 0%,transparent 65%)`,
        pointerEvents: 'none',
      }} />

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 28, height: 28, borderRadius: 8,
            background: 'linear-gradient(135deg,#22c55e30,#22c55e10)',
            border: '1px solid #22c55e35',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ color: '#22c55e', fontSize: 14, fontWeight: 900 }}>T</span>
          </div>
          <span style={{ color: '#fff', fontSize: 16, fontWeight: 800 }}>TradeReflex</span>
        </div>
        <span style={{ color: C.muted, fontSize: 11 }}>{today}</span>
        <span style={{ color: C.muted, fontSize: 11 }}>tradereflex.in</span>
      </div>

      {/* ── Hero row ── */}
      <SCard accent={color}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          {/* SVG donut */}
          <div style={{ position: 'relative', width: 90, height: 90, flexShrink: 0 }}>
            <svg viewBox="0 0 52 52" width="90" height="90" style={{ transform: 'rotate(-90deg)' }}>
              <circle cx="26" cy="26" r="21" fill="none" strokeWidth="5" stroke={C.border} />
              <circle cx="26" cy="26" r="21" fill="none" strokeWidth="5" stroke={color}
                strokeDasharray={`${dash} ${circumference}`} strokeLinecap="round" />
            </svg>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color, fontSize: 22, fontWeight: 900, lineHeight: 1 }}>{overall}</span>
              <span style={{ color: C.secondary, fontSize: 9 }}>/100</span>
            </div>
          </div>

          {/* Right side */}
          <div style={{ flex: 1 }}>
            {/* Badges */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
              <Pill label={scoreLabel(overall)} color={color} />
              <Pill label={`${tier.text} of traders`} color={tier.color} />
              {profile_type && <Pill label={profile_type} color="#818cf8" />}
            </div>

            {/* Milestone progress */}
            {next && prev && (
              <div style={{ marginBottom: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ color: C.secondary, fontSize: 10 }}>
                    <span style={{ color: C.primary, fontWeight: 700 }}>{prev.label}</span>
                    {' → '}
                    <span style={{ color: '#f59e0b', fontWeight: 700 }}>{next.label}</span>
                  </span>
                  <span style={{ color: C.secondary, fontSize: 10 }}>{next.threshold - overall} pts to go</span>
                </div>
                <div style={{ height: 6, borderRadius: 3, background: C.border, overflow: 'hidden' }}>
                  <div style={{ width: `${milestoneWidth}%`, height: '100%', background: 'linear-gradient(90deg,#f59e0b99,#f59e0b)', borderRadius: 3 }} />
                </div>
              </div>
            )}

            {/* Overall progress bar */}
            <div style={{ height: 6, borderRadius: 3, background: C.border, overflow: 'hidden' }}>
              <div style={{ width: `${overall}%`, height: '100%', background: `linear-gradient(90deg,${color}99,${color})`, borderRadius: 3 }} />
            </div>
            <div style={{ color: C.secondary, fontSize: 10, marginTop: 4 }}>Discipline Score · {today}</div>
          </div>
        </div>
      </SCard>

      {/* ── Component Breakdown ── */}
      <SDiv label="Component Breakdown" />
      <SCard>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 7 }}>
          {components.map(c => {
            const col = scoreColor(c.score);
            return (
              <div key={c.key} style={{
                padding: '7px 9px', borderRadius: 8,
                background: `${col}10`, border: `1px solid ${col}22`,
              }}>
                <div style={{ color: C.secondary, fontSize: 9, fontWeight: 600, marginBottom: 2 }}>{c.label}</div>
                <div style={{ color: col, fontSize: 14, fontWeight: 900, marginBottom: 4, lineHeight: 1 }}>{c.score}</div>
                <div style={{ height: 4, borderRadius: 2, background: C.border, overflow: 'hidden' }}>
                  <div style={{ width: `${c.score}%`, height: '100%', background: col, borderRadius: 2 }} />
                </div>
              </div>
            );
          })}
        </div>
      </SCard>

      {/* ── Strengths & Focus Areas ── */}
      <SDiv label="Strengths & Focus Areas" />
      <div style={{ display: 'flex', gap: 8 }}>
        {strong.length > 0 && (
          <SCard accent="#16C784" style={{ flex: 1 }}>
            <SHead emoji="✅" title="Strengths" color="#16C784" />
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              {strong.map(c => <Pill key={c.key} label={`${c.label} ${c.score}`} color="#16C784" />)}
            </div>
          </SCard>
        )}
        {weak.length > 0 && (
          <SCard accent="#f59e0b" style={{ flex: 1 }}>
            <SHead emoji="⚡" title="Focus Areas" color="#f59e0b" />
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              {weak.map(c => <Pill key={c.key} label={`${c.label} ${c.score}`} color={scoreColor(c.score)} />)}
            </div>
          </SCard>
        )}
      </div>

      {/* ── Stats strip ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8 }}>
        <MC label="Score"       value={overall}        color={color} />
        <MC label="Strengths"   value={strong.length}  color="#16C784" />
        <MC label="Focus Areas" value={weak.length}    color="#f59e0b" />
        <MC label="Tier"        value={tier.text}      color={tier.color} />
      </div>

      {/* ── Footer ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        paddingTop: 8, borderTop: `1px solid ${C.borderS}`, flexShrink: 0, marginTop: 'auto',
      }}>
        <span style={{ color: C.muted, fontSize: 10 }}>Know your edge · TradeReflex</span>
        <span style={{ color: C.secondary, fontSize: 10, fontWeight: 700 }}>tradereflex.in</span>
      </div>
    </div>
  );
}

export default DisciplineShareCard;
